/**
 * `?demo=summary-<mode>` fixtures: each phone summary screen's TV copy,
 * built with the phone's own section builders (app/LagKing/src/tv/
 * summarySections.ts) and realistic values for what that screen shows, so
 * the page and the Apple TV app can be checked without a phone. Colors are
 * the phone's theme tokens (app/LagKing/src/theme/tokens.ts).
 */
import type { TvPuttEvent, TvSnapshot, TvSummary, TvSummarySection } from './protocol';
import {
  challengeResultLayout,
  challengeSummaryLayout,
  tvLayout,
  tvRow,
  tvText,
  tvTextSection,
  TV_DIVIDER,
} from '../../../app/LagKing/src/tv/summarySections';
import { sim9PlayersTvLayout, sim9RoundTvLayout } from '../../../app/LagKing/src/tv/sim9RoundSections';
import {
  horseGameOverTv,
  ladderDuelRoundOverTv,
  lineFourGameOverTv,
  runBoardRows,
  runCardTvLayout,
  turfWarGameOverTv,
  baseballGameOverTv,
  footballGameOverTv,
} from '../../../app/LagKing/src/tv/gameOverSections';
import { modeRulesTvSummary } from '../../../app/LagKing/src/tv/rulesSummary';
import { MODE_TUTORIALS } from '../../../app/LagKing/src/tutorial/modeTutorials';
import { dailyStartLineTvSummary } from '../../../app/LagKing/src/screens/startLine/dailyStartLineModel';

/** app/LagKing/src/theme/tokens.ts colors. */
const C = {
  gold: '#C8922A',
  goldLight: '#DBA94D',
  darkNavy: '#0B1729',
  cardNavy: '#162B4D',
  targetRingBlue: '#2A4A7A',
  flagRed: '#C41E3A',
  statusBlue: '#4FC3F7',
  proximityGood: '#3FB950',
  proximityMid: '#E3B341',
  proximityFar: '#F0605D',
  white: '#FFFFFF',
  textPrimary: '#FFFFFF',
  textSecondary: '#B0BEC5',
  textMuted: '#829CA8',
} as const;

const HOUSE_PALETTE = { line: C.textMuted, pin: C.flagRed, outline: C.darkNavy, ring: C.targetRingBlue };
const START_LINES_PALETTE = {
  proximityGood: C.proximityGood,
  proximityMid: C.proximityMid,
  proximityFar: C.proximityFar,
  empty: C.cardNavy,
  count: C.textPrimary,
  axis: C.textSecondary,
  muted: C.textMuted,
};
const MATCH_BOARD_PALETTE = {
  label: C.textMuted,
  hole: C.gold,
  ft: C.textSecondary,
  par: C.textMuted,
  score: C.textPrimary,
  won: C.proximityGood,
  wonFill: 'rgba(63,185,80,0.22)',
  line: 'rgba(255,255,255,0.28)',
};
/** A Sim 9 round on Cedar Ridge: distances, pars, and each hole's putts
 *  (first putt, then the comeback when it missed). */
const SIM9_HOLES = [10, 22, 34, 16, 42, 28, 60, 36, 18];
const SIM9_PARS = [2, 2, 2, 2, 2, 2, 3, 2, 2];
function sim9Putt(requiredFt: number, distanceFt: number, angleDeg: number, holed = false) {
  const leaveFt = holed ? 0 : Math.abs(distanceFt - requiredFt);
  return { speedMps: 2, distanceFt, angleDeg, angleHighConfidence: true, requiredFt, leaveFt, holed, judgedStimpFt: 10 };
}
const SIM9_PUTTS = [
  [sim9Putt(10, 11.2, 0.4), sim9Putt(1.2, 1.4, 0.1, true)],
  [sim9Putt(22, 20.1, -1.3), sim9Putt(1.9, 2.1, 0.3, true)],
  [sim9Putt(34, 37.5, 1.8), sim9Putt(3.5, 3.9, -0.6), sim9Putt(0.4, 0.5, 0, true)],
  [sim9Putt(16, 16, 0.2, true)],
  [sim9Putt(42, 39.4, -0.8), sim9Putt(2.6, 2.8, 0.5, true)],
  [sim9Putt(28, 29.9, 0.9), sim9Putt(1.9, 2.0, -0.2, true)],
  [sim9Putt(60, 55.2, -2.4), sim9Putt(4.8, 5.1, 0.7, true)],
  [sim9Putt(36, 37.1, 0.3), sim9Putt(1.1, 1.3, 0, true)],
  [sim9Putt(18, 16.8, -0.5), sim9Putt(1.2, 1.3, 0.2, true)],
];
/** utils/proximityBands proximityRings for a 15-30 ft target. */
const RINGS_20FT = [
  { radiusFt: 2, color: C.proximityGood, fillOpacity: 0.18 },
  { radiusFt: 4, color: C.proximityMid, fillOpacity: 0.12 },
];

export const SUMMARY_DEMO_MODES = [
  'summary-distance',
  'summary-daily',
  'summary-match',
  'summary-sent',
  'summary-standings',
  'summary-results',
  'summary-poker',
  'summary-pokerdaily',
  'summary-pokersent',
  'summary-pokersettled',
  'summary-sim9sent',
  'summary-sim9match',
  'summary-koth',
  'summary-pace',
  'summary-sim9stroke',
  'summary-sim9response',
  'summary-dailystartline',
  'summary-startline',
  'summary-ladder',
  'summary-linefour',
  'summary-turfwar',
  'summary-rules',
  'summary-baseball',
  'summary-football',
  'summary-footballrules',
  'summary-ladderduel',
  'summary-horse',
  'summary-legacy',
] as const;
export type SummaryDemoMode = (typeof SUMMARY_DEMO_MODES)[number];

const EVENT: TvPuttEvent = {
  kind: 'putt',
  id: 'demo-summary',
  createdAtMs: Date.now() - 60_000,
  speedMps: 1.9,
  distanceFt: 19.4,
  angleDeg: 0.4,
  proximityFt: 0.8,
  targetFt: 20,
} as TvPuttEvent;

/** Five 20 ft putts: [distanceFt, angleDeg, primary, color, line, lineColor]. */
const DISTANCE_PUTTS: Array<[number, number, string, string, string, string]> = [
  [18.6, -0.9, '1.4 ft SHORT', C.proximityGood, '<- SLIGHT', C.proximityGood],
  [20.0, 0.1, 'MADE', C.proximityGood, 'PURE 👑', C.proximityGood],
  [22.9, 1.8, '3.0 ft LONG', C.proximityMid, 'WIDE -->', C.proximityMid],
  [19.2, -0.3, '0.8 ft SHORT', C.proximityGood, 'PURE 👑', C.proximityGood],
  [21.4, 0.7, '1.5 ft LONG', C.proximityGood, 'SLIGHT ->', C.proximityGood],
];

function distanceLayout(supplement: ReturnType<typeof tvText>[]) {
  return challengeSummaryLayout({
    hero: [
      tvText('AVERAGE PROXIMITY', C.textMuted, 'caption', 'bold', 0.15),
      tvText('1.3 ft', C.gold, 'hero', 'heavy'),
      tvText('at 20 ft', C.textSecondary, 'title'),
      tvText('Personal Best: 1.1 ft', C.textSecondary, 'body'),
    ],
    supplement,
    putts: {
      header: { left: 'PUTT BY PUTT', right: 'TARGET 20 FT' },
      rows: DISTANCE_PUTTS.map(([distanceFt, , primary, color, line, lineColor], index) => ({
        index: `#${index + 1}`,
        primary,
        secondary: `${distanceFt.toFixed(1)} ft TOTAL`,
        color,
        line,
        lineColor,
      })),
    },
    house: {
      balls: DISTANCE_PUTTS.map(([distanceFt, angleDeg], index) => ({
        side: 'challenger' as const,
        order: index + 1,
        longitudinalMissFt: 20 - distanceFt,
        lateralMissFt: Math.tan((angleDeg * Math.PI) / 180) * distanceFt,
        color: C.gold,
      })),
      rings: RINGS_20FT,
      lateralExaggeration: 1,
      headroomFactor: 1.02,
      edgePaddingPx: 4,
      showRingLabels: true,
      ringLabelPosition: 'top',
      unitPerFt: 1,
      unitLabel: 'ft',
      palette: HOUSE_PALETTE,
    },
    startLines: { angles: DISTANCE_PUTTS.map(([, angleDeg]) => angleDeg), palette: START_LINES_PALETTE },
  });
}

/** A second and third player's Sim 9 round: the fixture round nudged. */
function shiftedPutts(delta: number, angleDelta: number) {
  return SIM9_PUTTS.map((hole) => hole.map((putt) => sim9Putt(putt.requiredFt, putt.distanceFt + delta, putt.angleDeg + angleDelta, putt.holed)));
}
const SIM9_TV_COMMON = {
  holeDistancesFt: SIM9_HOLES,
  pars: SIM9_PARS,
  colors: { gold: C.gold, textSecondary: C.textSecondary, textMuted: C.textMuted, textPrimary: C.textPrimary },
  palettes: { house: HOUSE_PALETTE, startLines: START_LINES_PALETTE, matchBoard: MATCH_BOARD_PALETTE },
  unitPerFt: 1,
  unitLabel: 'ft',
  formatFt: (ft: number) => `${ft.toFixed(1)} ft`,
};
/** app/LagKing/src/sim9/ballColors SIM9_BALL_COLORS. */
const BALL = ['#C8922A', '#4FC3F7', '#F0605D', '#3FB950'];
const GAME_COLORS = { gold: C.gold, textPrimary: C.textPrimary, textSecondary: C.textSecondary, textMuted: C.textMuted };
const VERSUS_COLORS = { ...GAME_COLORS, empty: C.darkNavy, line: 'rgba(255,255,255,0.18)', ring: C.white };
/** A run's start lines (degrees). */
const RUN_ANGLES = [0.3, -0.6, 0.1, 1.2, -0.2, 0.5, -1.4, 0.0, 0.8, -2.3];

function summaryFor(mode: SummaryDemoMode): TvSummary {
  // The legacy card fields every phone still sends (older TVs draw these).
  const legacy = { kicker: 'ROUND COMPLETE', title: 'Round complete', rows: [] };
  switch (mode) {
    case 'summary-distance':
      return {
        ...legacy,
        title: '20 ft Challenge',
        ...distanceLayout([
          tvText('GLOBAL RANK', C.textMuted, 'caption', 'bold', 0.15),
          tvRow(tvText('THIS ROUND', C.textSecondary, 'caption', 'bold', 0.07), '#14', C.gold, 'title', 'heavy'),
          tvRow(tvText('YOUR BEST · 1.1 FT', C.textSecondary, 'caption', 'bold', 0.07), '#9', C.textPrimary, 'body', 'bold'),
          tvText('OF 212 AT 20 FT', C.textMuted, 'caption'),
        ]),
      };
    case 'summary-daily': {
      const layout = distanceLayout([
        tvText("TODAY'S RANK", C.textMuted, 'caption', 'heavy', 0.12),
        tvText('#3 of 41', C.gold, 'title', 'heavy'),
        tvText('1.29 ft avg', C.textSecondary, 'body'),
      ]);
      // The daily passes no personal best.
      const hero = layout.sections![0];
      if (hero.kind === 'text') hero.lines = hero.lines.filter((line) => !line.text.startsWith('Personal Best'));
      return { ...legacy, title: '20 ft Challenge', ...layout };
    }
    case 'summary-match':
      return {
        ...legacy,
        kicker: 'DISTANCE MATCH COMPLETE',
        title: 'YOU WIN',
        ...challengeResultLayout({
          header: [tvText('YOU WIN', C.proximityGood, 'value', 'heavy', 0.07), tvText('25 ft', C.textMuted, 'body', 'bold', 0.12)],
          versus: {
            separator: 'vs',
            boxes: [
              { label: '', handle: '@cole', value: '1.8', unit: 'ft', valueColor: C.proximityGood, border: C.proximityGood },
              { label: '', handle: '@kellyf', value: '2.6', unit: 'ft', valueColor: C.textPrimary, border: null },
            ],
          },
          house: {
            balls: [
              [1, 1.2, 0.3, 'challenger'], [2, -0.8, -0.4, 'challenger'], [3, 2.5, 0.9, 'challenger'], [4, 0.4, 0.1, 'challenger'], [5, -1.9, 0.6, 'challenger'],
              [1, 2.8, -0.7, 'opponent'], [2, -1.6, 1.1, 'opponent'], [3, 0.9, -0.2, 'opponent'], [4, 3.4, 0.5, 'opponent'], [5, -2.2, -1.0, 'opponent'],
            ].map(([order, longFt, latFt, side]) => ({
              side: side as 'challenger' | 'opponent',
              order: order as number,
              longitudinalMissFt: longFt as number,
              lateralMissFt: latFt as number,
              color: side === 'challenger' ? C.gold : C.targetRingBlue,
              ...(side === 'challenger' && order === 4 ? { halo: true } : {}),
            })),
            rings: RINGS_20FT,
            lateralExaggeration: 1,
            headroomFactor: 1.02,
            edgePaddingPx: 4,
            showRingLabels: true,
            ringLabelPosition: 'right',
            unitPerFt: 1,
            unitLabel: 'ft',
            palette: HOUSE_PALETTE,
          },
          plotNote: [tvText('Gold: you · Blue: @kellyf · Number = putt', C.textMuted, 'caption')],
          startLines: [
            { title: 'START LINE · @cole', chart: { angles: [-0.9, 0.1, 1.8, -0.3, 0.7], scaleMaxCount: 2, palette: START_LINES_PALETTE }, unavailable: [] },
            { title: 'START LINE · @kellyf', chart: { angles: [0.3, -2.1, 0.9, 1.2, -0.6], scaleMaxCount: 2, palette: START_LINES_PALETTE }, unavailable: [] },
          ],
          footer: [tvText('Closest average proximity wins.', C.textMuted, 'caption')],
          rank: [tvText('GLOBAL RANK', C.textMuted, 'caption', 'bold', 0.15), tvRow(tvText('THIS ROUND', C.textSecondary, 'caption', 'bold', 0.07), '#12', C.gold, 'title', 'heavy'), tvText('OF 340 AT 25 FT', C.textMuted, 'caption')],
        }),
      };
    case 'summary-sent':
      return {
        ...legacy,
        kicker: 'CHALLENGE SENT',
        ...tvLayout([
          tvTextSection(0, [
            tvText('CHALLENGE SENT', C.gold, 'caption', 'bold', 0.25),
            tvText('@kellyf will race your round putt for putt.', C.textPrimary, 'title', 'bold'),
          ]),
          tvTextSection(1, [
            tvText('Your round', C.textMuted, 'caption', 'bold', 0.16),
            tvText('1.6 ft', C.gold, 'hero', 'heavy'),
            tvText('avg proximity · 1 made', C.textSecondary, 'body'),
            tvText("Ranks #12 on 20' leaderboard", C.gold, 'body', 'bold'),
          ], { card: true }),
          tvTextSection(0, [tvText("They have 7 days to respond. You'll get a notification when they do.", C.textMuted, 'caption')]),
        ]),
      };
    case 'summary-standings':
      return {
        ...legacy,
        kicker: 'PASS & PLAY',
        ...tvLayout([
          tvTextSection(0, [tvText('STANDINGS', C.gold, 'value', 'heavy', 0.14), tvText('Cumulative points this session', C.textMuted, 'body')]),
          {
            kind: 'entries',
            column: 1,
            rows: [['Cole', 9], ['Kelly', 7], ['Sam', 4]].map(([name, points], index) => {
              const leader = index === 0;
              return {
                rank: String(index + 1),
                rankColor: leader ? C.gold : C.textMuted,
                name: `${name}${leader ? ' 👑' : ''}`,
                nameColor: C.textPrimary,
                value: String(points),
                valueColor: leader ? C.gold : C.textPrimary,
                valueLabel: 'PTS',
                border: leader ? C.gold : null,
              };
            }),
          },
        ], [0.8, 1.4]),
      };
    case 'summary-results':
      return {
        ...legacy,
        kicker: 'ROUND RESULTS',
        ...tvLayout([
          tvTextSection(0, [tvText('ROUND RESULTS', C.gold, 'value', 'heavy', 0.14), tvText('20 ft', C.textMuted, 'body')]),
          {
            kind: 'entries',
            column: 1,
            rows: [
              ['Cole', 1.4, 3, 1],
              ['Kelly', 2.1, 2, 0],
              ['Sam', 3.3, 1, 0],
            ].map(([name, avg, points, made], index) => {
              const winner = index === 0;
              return {
                rank: String(index + 1),
                rankColor: winner ? C.gold : C.textMuted,
                name: `${name}${winner ? ' 👑' : ''}`,
                nameColor: C.textPrimary,
                detail: `+${points} pts${(made as number) > 0 ? `  ·  ${made} made` : ''}`,
                detailColor: C.textMuted,
                value: (avg as number).toFixed(1),
                valueColor: winner ? C.gold : C.textPrimary,
                valueLabel: 'FT AVG',
                border: winner ? C.gold : null,
              };
            }),
          },
        ], [0.8, 1.4]),
      };
    case 'summary-poker':
      return {
        ...legacy,
        title: 'Poker',
        ...tvLayout([
          tvTextSection(0, [tvText('Cole WINS', C.gold, 'value', 'heavy', 0.02)]),
          pokerHand(1, 'Cole', 'Full House, Queens over Tens', true, ['Q♠', 'Q♥', 'Q♦', '10♣', '10♥'], 'Best 5 of 7 cards'),
          pokerHand(2, 'Kelly', 'Two Pair, Kings and Fives', false, ['K♠', 'K♦', '5♣', '5♥', 'A♠'], null),
        ], [0.8, 1, 1]),
      };
    case 'summary-pokerdaily':
      return {
        ...legacy,
        kicker: 'DAILY POKER',
        ...tvLayout([
          {
            kind: 'hand',
            column: 0,
            lines: [tvText('YOUR HAND', C.textSecondary, 'caption', 'bold', 0.09), tvText('Flush', C.gold, 'value', 'heavy', 0.02)],
            cards: ['A♥', 'Q♥', '10♥', '9♠', '7♥', '4♥', '2♦'],
            dim: [false, false, false, true, false, false, true],
            empty: 'No cards earned',
          },
          tvTextSection(1, [tvText('CURRENT DAILY RANK: #6 of 58', C.textSecondary, 'body')]),
        ], [1.4, 1]),
      };
    case 'summary-pokersent':
      return {
        ...legacy,
        kicker: 'POKER CHALLENGE',
        ...tvLayout([
          {
            kind: 'hand',
            column: 0,
            lines: [tvText('YOUR HAND', C.textSecondary, 'caption', 'bold', 0.09), tvText('Three of a Kind', C.gold, 'value', 'heavy', 0.02)],
            cards: ['J♠', 'J♦', 'J♣', '8♥', '3♠'],
            dim: null,
            empty: 'No cards earned',
          },
          tvTextSection(0, [tvText('They play the same five off the same deck. You will see the result when they finish.', C.textSecondary, 'body')]),
          // PokerChallengeCompare pokerSentTvSections: your round's numbers,
          // shots, and per-hole leaves (2026-09-30).
          {
            kind: 'metrics',
            column: 1,
            perRow: 2,
            tiles: [
              { label: 'AVG LEAVE', value: '1.6 ft', valueColor: C.gold },
              { label: 'HOLES MADE', value: '1 of 5' },
            ],
          },
          {
            kind: 'house',
            column: 1,
            balls: [[1, 0.9, 0.3], [2, 0, 0], [3, 2.4, -0.5], [4, -1.6, 0.2], [5, 1.2, -0.1]].map(([order, longFt, latFt]) => ({
              side: 'challenger' as const,
              order,
              longitudinalMissFt: longFt,
              lateralMissFt: latFt,
              color: C.gold,
            })),
            rings: [{ radiusFt: 3 }, { radiusFt: 6 }, { radiusFt: 10 }],
            lateralExaggeration: 2.5,
            headroomFactor: 1.2,
            edgePaddingPx: 28,
            showRingLabels: true,
            ringLabelPosition: 'right',
            unitPerFt: 1,
            unitLabel: 'ft',
            palette: HOUSE_PALETTE,
            legend: [{ color: C.gold, text: 'YOUR SHOTS' }],
            legendHint: 'Numbers are hole numbers',
            legendColor: C.textMuted,
          },
          {
            kind: 'holeLeaves',
            column: 1,
            holes: [[12, '0.9'], [18, 'IN'], [25, '2.4'], [31, '1.6'], [40, '1.2']].map(([ft, a], i) => ({ label: `H${i + 1} · ${ft}′`, a: a as string })),
            labelColor: C.textMuted,
            colorA: C.gold,
            colorB: C.statusBlue,
            dividerColor: C.textMuted,
          },
        ], [1.15, 1]),
      };
    case 'summary-pokersettled':
      return {
        ...legacy,
        kicker: 'POKER CHALLENGE',
        ...tvLayout([
          {
            kind: 'hand',
            column: 0,
            lines: [tvRow(tvText('YOUR HAND · STRAIGHT', C.textSecondary, 'caption', 'bold', 0.09), 'WINNER', C.gold, 'title', 'heavy')],
            cards: ['9♠', '8♦', '7♣', '6♥', '5♠'],
            dim: null,
            empty: null,
          },
          {
            kind: 'hand',
            column: 0,
            lines: [tvRow(tvText("KELLYF'S HAND · TWO PAIR", C.textSecondary, 'caption', 'bold', 0.09), '', C.gold, 'title', 'heavy')],
            cards: ['K♠', 'K♦', '4♣', '4♥', 'J♠'],
            dim: null,
            empty: null,
          },
          tvTextSection(0, [tvText('AVG LEAVE · YOU 1.9 FT · @KELLYF 2.4 FT', C.textSecondary, 'caption', 'heavy', 0.05)]),
          {
            kind: 'house',
            column: 1,
            balls: [
              [1, 0.8, 0.2, 'challenger'], [2, 0, 0, 'challenger'], [3, 2.6, -0.4, 'challenger'], [4, -1.4, 0.3, 'challenger'], [5, 1.1, -0.2, 'challenger'],
              [1, 1.9, -0.5, 'opponent'], [2, -2.3, 0.4, 'opponent'], [3, 0.7, 0.1, 'opponent'], [4, 3.2, 0.6, 'opponent'], [5, -0.9, -0.3, 'opponent'],
            ].map(([order, longFt, latFt, side]) => ({
              side: side as 'challenger' | 'opponent',
              order: order as number,
              longitudinalMissFt: longFt as number,
              lateralMissFt: latFt as number,
              color: side === 'challenger' ? C.gold : C.statusBlue,
            })),
            rings: [{ radiusFt: 1 }, { radiusFt: 3 }, { radiusFt: 5 }, { radiusFt: 10 }],
            lateralExaggeration: 2.5,
            headroomFactor: 1.2,
            edgePaddingPx: 28,
            showRingLabels: true,
            ringLabelPosition: 'right',
            unitPerFt: 1,
            unitLabel: 'ft',
            palette: HOUSE_PALETTE,
            legend: [{ color: C.gold, text: 'YOU' }, { color: C.statusBlue, text: '@KELLYF' }],
            legendHint: 'Numbers are hole numbers',
            legendColor: C.textMuted,
          },
          {
            kind: 'holeLeaves',
            column: 1,
            holes: [[12, '0.8', '2.0'], [18, 'IN', '2.3'], [25, '2.6', '0.7'], [31, '1.4', '3.3'], [40, '1.1', '1.0']].map(([ft, a, b], i) => ({
              label: `H${i + 1} · ${ft}′`,
              a: a as string,
              b: b as string,
            })),
            labelColor: C.textMuted,
            colorA: C.gold,
            colorB: C.statusBlue,
            dividerColor: C.textMuted,
          },
        ], [1.1, 1]),
      };
    case 'summary-sim9sent':
      // Sim9Host sim9SentSummary: the sent text across the top, the
      // scorecard, then the round stats (sim9RoundTvLayout, 2026-09-30).
      return {
        ...legacy,
        kicker: 'CHALLENGE SENT',
        ...sim9RoundTvLayout({
          header: [
            tvText('CHALLENGE SENT', C.gold, 'title', 'heavy', 0.08),
            tvText('Cedar Ridge · 19 strokes · +1', C.textPrimary, 'body', 'bold'),
            tvText('#4 of 37 on Cedar Ridge', C.gold, 'body', 'bold'),
            tvText("@kellyf has 7 days to answer your round. You'll get a notification when the match is decided.", C.textSecondary, 'caption', 'bold'),
          ],
          holeDistancesFt: SIM9_HOLES,
          pars: SIM9_PARS,
          strokes: [2, 2, 3, 2, 2, 2, 2, 2, 2],
          putts: SIM9_PUTTS,
          playerLabel: 'YOU',
          playerColor: C.gold,
          colors: { gold: C.gold, textSecondary: C.textSecondary, textMuted: C.textMuted },
          palettes: { house: HOUSE_PALETTE, startLines: START_LINES_PALETTE, matchBoard: MATCH_BOARD_PALETTE },
          unitPerFt: 1,
          unitLabel: 'ft',
          formatFt: (ft) => `${ft.toFixed(1)} ft`,
        }),
      };
    case 'summary-sim9match':
      return {
        ...legacy,
        kicker: 'SIM 9 MATCH COMPLETE',
        ...tvLayout([
          tvTextSection(0, [
            tvText('YOU WIN 2&1', C.proximityGood, 'value', 'heavy', 0.05),
            tvText('Cedar Ridge · @cole vs @kellyf', C.textSecondary, 'body'),
          ]),
          {
            kind: 'matchBoard',
            column: 0,
            names: ['cole', 'kellyf'],
            holes: [10, 22, 34, 16, 42, 28, 60, 36, 18],
            pars: [2, 2, 2, 2, 2, 2, 3, 2, 2],
            strokesA: [2, 1, 2, 3, 2, 2, 3, 2, null],
            strokesB: [2, 2, 3, 2, 3, 2, 4, 3, null],
            palette: {
              label: C.textMuted,
              hole: C.gold,
              ft: C.textSecondary,
              par: C.textMuted,
              score: C.textPrimary,
              won: C.proximityGood,
              wonFill: 'rgba(63,185,80,0.22)',
              line: 'rgba(255,255,255,0.28)',
            },
          },
        ]),
      };
    case 'summary-sim9stroke':
      // Sim9StrokePlayScreen at round done: four players (2026-09-30).
      return {
        ...legacy,
        kicker: 'SIM 9 · FINAL',
        ...sim9PlayersTvLayout({
          ...SIM9_TV_COMMON,
          header: [
            tvText('COLE WINS', C.gold, 'title', 'heavy', 0.04),
            tvText('CEDAR RIDGE · FINAL', C.textSecondary, 'caption', 'bold', 0.12),
          ],
          standingsTitle: 'STANDINGS',
          players: [
            { label: 'Cole', color: BALL[0], strokes: [2, 2, 3, 1, 2, 2, 2, 2, 2], putts: SIM9_PUTTS, rank: '1', winner: true },
            { label: 'Kelly', color: BALL[1], strokes: [2, 3, 3, 2, 2, 2, 3, 2, 2], putts: shiftedPutts(1.2, 0.4), rank: '2' },
            { label: 'Dad', color: BALL[2], strokes: [3, 2, 3, 2, 3, 2, 3, 2, 2], putts: shiftedPutts(-1.8, -0.7), rank: 'T3' },
            { label: 'Stu', color: BALL[3], strokes: [2, 3, 3, 2, 3, 2, 3, 2, 2], putts: shiftedPutts(2.4, 1.1), rank: 'T3' },
            { label: 'Andre', color: '#B388FF', strokes: [3, 3, 3, 2, 3, 2, 3, 3, 2], putts: shiftedPutts(-2.6, 0.9), rank: '5' },
            { label: 'Mom', color: '#FFFFFF', strokes: [3, 3, 2, 2, null, null, null, null, null], putts: shiftedPutts(0.9, -1.2).slice(0, 4), rank: null },
          ],
        }),
      };
    case 'summary-sim9response':
      // Sim9MatchResponseScreen at match done: both scorecards, both players'
      // numbers, your round stats, and each start-line chart.
      return {
        ...legacy,
        kicker: 'SIM 9 MATCH · FINAL',
        ...sim9PlayersTvLayout({
          ...SIM9_TV_COMMON,
          header: [
            tvText('YOU WIN 2&1', C.gold, 'title', 'heavy', 0.04),
            tvText('CEDAR RIDGE · vs @kellyf', C.textSecondary, 'caption', 'bold', 0.12),
          ],
          standingsTitle: 'MATCH',
          focusIndex: 1,
          players: [
            { label: '@kellyf', color: BALL[1], strokes: [2, 2, 3, 2, 3, 2, 4, 3, null], putts: shiftedPutts(1.5, 0.6).slice(0, 8) },
            { label: 'You', color: BALL[0], strokes: [2, 1, 2, 3, 2, 2, 3, 2, null], putts: SIM9_PUTTS.slice(0, 8), winner: true },
          ],
        }),
      };
    case 'summary-dailystartline':
      // DailyStartLineResult (DAILY_START_LINE.md).
      return dailyStartLineTvSummary({
        result: { streak: 7, distanceFt: 12, endReason: 'miss', status: 'done', angles: RUN_ANGLES.slice(0, 8) },
        dayRank: '#3 of 41',
        allTime: { headline: '#18 of 402', sub: 'Your best: #9, 11 in a row' },
        board: [
          { rank: 1, name: 'kellyf', value: '12', you: false },
          { rank: 2, name: 'stu', value: '9', you: false },
          { rank: 3, name: 'cole', value: '7', you: true },
          { rank: 4, name: 'dadputts', value: '5', you: false },
          { rank: 5, name: 'amherstace', value: '3', you: false },
        ],
        colors: GAME_COLORS,
        palette: START_LINES_PALETTE,
      });
    case 'summary-startline':
      // StartLineStreakLandscape's run card, counting down to PUTT AGAIN.
      return {
        ...legacy,
        kicker: '8 FT STREAK',
        title: '9',
        countdownEndsAtMs: Date.now() + 5_000,
        countdownLabel: 'Putt again',
        ...runCardTvLayout({
          eyebrow: 'START LINE · 8 FT STREAK',
          value: '9',
          valueLabel: 'IN A ROW',
          detail: 'NEW PERSONAL BEST',
          tiles: [
            { label: 'BEST', value: '9' },
            { label: 'DISTANCE', value: '8 FT' },
            { label: 'AVG START LINE', value: '0.74°', sub: 'Off center, either side' },
            { label: 'PUTTS', value: '10' },
          ],
          startLines: { title: 'START LINES · THIS RUN', angles: RUN_ANGLES, palette: START_LINES_PALETTE },
          board: {
            title: '8 FT LEADERBOARD',
            rows: runBoardRows([
              { rank: 1, name: 'kellyf', value: '14' },
              { rank: 2, name: 'You', value: '9', you: true },
              { rank: 3, name: 'stu', value: '8' },
              { rank: 4, name: 'dadputts', value: '6' },
            ], GAME_COLORS),
          },
          colors: GAME_COLORS,
        }),
      };
    case 'summary-ladder':
      // LadderSummary's run card, counting down to RUN AGAIN.
      return {
        ...legacy,
        kicker: 'RUN COMPLETE',
        title: '6 CLIMBS',
        countdownEndsAtMs: Date.now() + 10_000,
        countdownLabel: 'Running again',
        ...runCardTvLayout({
          eyebrow: 'PUTTING LADDER · 10–40 FT',
          value: '6',
          valueLabel: 'CLIMBS',
          detail: 'Fell short · Putt: 31.2 ft  •  Needed 32.0 ft',
          detailColor: C.proximityFar,
          tiles: [
            { label: 'FURTHEST', value: '32.0 ft' },
            { label: 'AVG CLIMB', value: '3.5 ft' },
            { label: 'SHORTEST CLIMB', value: '1.1 ft' },
            { label: 'PERSONAL BEST', value: '8', valueColor: C.gold },
            { label: 'RANK', value: '#6 of 58' },
            { label: 'OPENING PUTT', value: '11.2 ft' },
          ],
          putts: {
            header: { left: 'CLIMB BY CLIMB', right: '10–40 FT' },
            rows: [14.8, 18.9, 22.4, 25.0, 29.3, 32.0].map((ft, index) => ({
              index: String(index + 1),
              primary: `${ft.toFixed(1)} ft`,
              color: C.textPrimary,
              line: '',
              lineColor: C.textMuted,
            })),
          },
          board: {
            title: 'LEADERBOARD · 10–40 FT',
            rows: runBoardRows([
              { rank: 1, name: 'kellyf', value: '11' },
              { rank: 2, name: 'stu', value: '10' },
              { rank: 3, name: 'cole', value: '8' },
              { rank: 4, name: 'dadputts', value: '7' },
            ], GAME_COLORS),
          },
          colors: GAME_COLORS,
        }),
      };
    case 'summary-linefour': {
      // LineFourHost at the result: Cole's diagonal on a 7 x 6 board.
      const board: Array<Array<0 | 1 | null>> = [
        [0, 1, 0, 1, 1, null, null],
        [1, 0, 1, 0, null, null, null],
        [0, 1, 0, 1, null, null, null],
        [1, 0, null, 0, null, null, null],
        [null, null, null, null, null, null, null],
        [null, null, null, null, null, null, null],
      ];
      return lineFourGameOverTv({
        names: ['Cole', 'Kelly'],
        playerColors: [C.gold, C.statusBlue],
        state: {
          board,
          winningCells: [{ row: 0, column: 0 }, { row: 1, column: 1 }, { row: 2, column: 2 }, { row: 3, column: 3 }],
          winnerIndex: 0,
          isDraw: false,
          attemptCount: 17,
        },
        putts: [9, 8],
        colors: VERSUS_COLORS,
      });
    }
    case 'summary-baseball':
      return baseballGameOverTv({
        names: ['Kelly', 'Cole'],
        playerColors: [C.proximityFar, '#ffffff'],
        runs: [2, 3],
        lineScore: [[0, 1, 1], [2, 1]],
        hits: [5, 6],
        blocks: [3, 2],
        doublePlays: [1, 0],
        winnerIndex: 1,
        inningsPlayed: 3,
        scheduledInnings: 3,
        swingOff: false,
        colors: VERSUS_COLORS,
      });
    case 'summary-football':
      return footballGameOverTv({
        names: ['Kelly', 'Cole'],
        playerColors: [C.proximityFar, '#ffffff'],
        scores: [10, 17],
        // Results spelled out (2026-10-18): the longest ones shrink to fit.
        drives: [['7', 'TURNOVER ON DOWNS', 'BLOCKED FIELD GOAL'], ['3', 'INTERCEPTION', '7']],
        yards: [142, 188],
        touchdowns: [1, 2],
        fieldGoals: [1, 1],
        turnovers: [1, 0],
        winnerIndex: 1,
        overtime: 0,
        colors: VERSUS_COLORS,
      });
    case 'summary-footballrules':
      return modeRulesTvSummary(MODE_TUTORIALS.football, C);
    case 'summary-rules':
      return modeRulesTvSummary(MODE_TUTORIALS.baseball, C);
    case 'summary-turfwar':
      return turfWarGameOverTv({
        names: ['Cole', 'Kelly'],
        playerColors: [C.proximityFar, C.statusBlue],
        score: [3, 2],
        winnerIndex: 0,
        standings: [
          { zoneIndex: 0, counts: [2, 1], controllerIndex: 0 },
          { zoneIndex: 1, counts: [0, 2], controllerIndex: 1 },
          { zoneIndex: 2, counts: [1, 1], controllerIndex: null },
          { zoneIndex: 3, counts: [1, 0], controllerIndex: 0 },
          { zoneIndex: 4, counts: [1, 0], controllerIndex: 0 },
        ],
        shots: [
          ...Array.from({ length: 5 }, (_, i) => ({ playerIndex: 0 as const, zoneIndex: i === 2 ? null : i })),
          ...Array.from({ length: 5 }, (_, i) => ({ playerIndex: 1 as const, zoneIndex: i < 4 ? [0, 1, 1, 2][i] : null })),
        ],
        suddenDeathZoneIndex: null,
        colors: { ...VERSUS_COLORS, ring: C.goldLight },
      });
    case 'summary-ladderduel':
      return {
        ...ladderDuelRoundOverTv({
          names: ['Cole', 'Computer'],
          playerColors: [C.gold, C.statusBlue],
          roundNumber: 3,
          eyebrow: 'ROUND OVER',
          headline: 'Computer busted past 40 ft',
          winnerIndex: 0,
          pot: 5,
          scores: [9, 4],
          climbs: [7, 6],
          putts: [10, 9],
          rangeLabel: '15–40 FT',
          colors: GAME_COLORS,
        }),
        countdownEndsAtMs: Date.now() + 10_000,
        countdownLabel: 'NEXT ROUND',
      };
    case 'summary-horse':
      return horseGameOverTv({
        names: ['Cole', 'Kelly'],
        playerColors: [C.gold, C.statusBlue],
        word: 'HORSE',
        loserIndex: 1,
        spelled: ['HO', 'HORSE'],
        tallies: [{ putts: 14, makes: 3, hits: 7 }, { putts: 14, makes: 1, hits: 5 }],
        colors: GAME_COLORS,
      });
    case 'summary-legacy':
      // A summary without sections (LeaderboardScreen's board card): the
      // shared layout converts it (summaryAsLayout).
      return {
        kicker: 'DISTANCE LEADERBOARD',
        title: '20 FT',
        rows: [],
        board: {
          title: '20 FT',
          rows: [
            { rank: 1, name: 'kellyf', value: '0.92 ft' },
            { rank: 2, name: 'stu', value: '1.08 ft' },
            { rank: 3, name: 'dadputts', value: '1.21 ft' },
            { rank: 4, name: 'amherstace', value: '1.30 ft' },
            { rank: 12, name: 'cole', value: '1.77 ft', you: true },
          ],
        },
        footer: 'Choose on your device.',
      };
    case 'summary-koth':
      return {
        ...legacy,
        kicker: 'YOU TOOK THE CROWN',
        ...tvLayout([
          tvTextSection(0, [tvText('King of the Hill', C.textPrimary, 'title', 'heavy'), tvText('September 2026', C.textSecondary, 'body')], { align: 'left' }),
          tvTextSection(0, [
            tvText('👑', undefined, 'value'),
            tvText('YOU TOOK THE CROWN', C.gold, 'title', 'heavy', 0.07),
            tvText('8.4 ft total', C.textPrimary, 'value', 'heavy'),
            tvText('Dethroned @kellyf. Now defend it.', C.textSecondary, 'body'),
            tvText("That was today's attempt. Same five holes tomorrow.", C.textMuted, 'body'),
          ], { card: true, border: C.gold }),
          tvTextSection(1, [
            tvText('THE HILL · SEPTEMBER 2026', C.gold, 'caption', 'heavy', 0.12),
            tvRow(tvText('👑 KING', C.textMuted, 'caption', 'heavy', 0.08), 'You · 8.4 ft', C.textPrimary, 'body', 'bold'),
            tvRow(tvText('YOUR BEST', C.textMuted, 'caption', 'heavy', 0.08), '8.4 ft', C.textPrimary, 'body', 'bold'),
          ], { card: true, align: 'left' }),
        ]),
      };
    case 'summary-pace':
      return {
        ...legacy,
        kicker: 'SESSION COMPLETE',
        ...tvLayout([
          tvTextSection(0, [
            tvText('SESSION COMPLETE', C.gold, 'eyebrow', 'heavy', 0.2),
            tvText('PACE & DISPERSION', C.textPrimary, 'title', 'heavy', 0.02),
            tvText('20.0 ft target · projected flat-green outcomes', C.textSecondary, 'caption'),
          ], { align: 'left' }),
          {
            kind: 'pacePlot',
            column: 0,
            card: true,
            points: PACE_POINTS,
            goalFraction: 0.1,
            ellipse: { center: { lateralFraction: 0.012, paceFraction: -0.018 }, semiMajorFraction: 0.11, semiMinorFraction: 0.05, rotationDeg: 12 },
            centroid: { lateralFraction: 0.012, paceFraction: -0.018 },
            palette: {
              disc: C.darkNavy,
              ring: C.targetRingBlue,
              goal: C.proximityGood,
              goalFill: 'rgba(63,185,80,0.08)',
              ellipse: C.gold,
              ellipseFill: 'rgba(200,146,42,0.10)',
              make: C.proximityGood,
              first: C.goldLight,
              dot: C.white,
              outline: C.darkNavy,
              label: C.textSecondary,
            },
            footnote: tvText('Each dot is the combined pace + projected-line leave. The green ring is your goal.', C.textMuted, 'caption'),
          },
          tvTextSection(1, [
            tvRow(tvText('GOAL ZONE', C.textSecondary, 'eyebrow', 'heavy', 0.13), '10%', C.textPrimary, 'title', 'heavy'),
            tvText('Within 10% of each putt length', C.textPrimary, 'body', 'bold'),
          ], { card: true, align: 'left' }),
          {
            kind: 'metrics',
            column: 1,
            perRow: 2,
            tiles: [
              ['GOAL RATE', '58%', '7/12 inside 10%'],
              ['AVERAGE PROXIMITY', '1.9 ft', '9.4% of target'],
              ['MEDIAN PROXIMITY', '1.6 ft', '8.0% of target'],
              ['PACE BIAS', '0.4 ft SHORT', '58% short · 42% long'],
              ['PATTERN CENTER', '1.2% RIGHT', '1.8% SHORT'],
              ['DISPERSION', 'MODELED 80% ELLIPSE', 'n=12 · descriptive model only'],
            ].map(([label, value, sub]) => ({ label, value, sub, labelColor: C.textSecondary, valueColor: C.textPrimary, subColor: C.textMuted })),
          },
        ], [1, 1.1]),
      };
  }
}

const PACE_POINTS = [
  [0.02, -0.05, false], [-0.03, 0.04, false], [0.0, 0.0, true], [0.05, -0.02, false], [-0.01, -0.08, false], [0.04, 0.06, false],
  [-0.06, -0.01, false], [0.01, 0.03, false], [0.03, -0.11, false], [-0.02, 0.09, false], [0.0, 0.0, true], [0.07, -0.04, false],
].map(([lateral, pace, made]) => ({ lateralMissFraction: lateral as number, paceMissFraction: pace as number, isMake: made as boolean }));

function pokerHand(
  column: number,
  name: string,
  description: string,
  winner: boolean,
  cards: string[],
  note: string | null,
): TvSummarySection {
  return {
    kind: 'hand',
    column,
    card: true,
    ...(winner ? { border: C.gold } : {}),
    lines: [tvRow(tvText(name, C.textPrimary, 'title', 'heavy'), description, winner ? C.gold : C.textSecondary, 'body', 'bold')],
    cards,
    empty: 'No cards',
    after: note ? [tvText(note, C.textMuted, 'caption', 'bold')] : null,
  };
}

export function isSummaryDemoMode(mode: string): mode is SummaryDemoMode {
  return (SUMMARY_DEMO_MODES as readonly string[]).includes(mode);
}

/** The snapshot a phone publishes while that summary screen is up. */
export function summaryFixture(mode: SummaryDemoMode): { snapshot: TvSnapshot; event: TvPuttEvent } {
  const summary = summaryFor(mode);
  void TV_DIVIDER;
  return {
    snapshot: {
      mode: 'challenge',
      title: summary.title,
      playerName: 'COLE',
      gateConnected: true,
      scene: null,
      sleep: 'touch',
      presentation: { kind: 'card', routeId: `demo.${mode}`, inputOwner: 'phone' },
      summary,
    } as TvSnapshot,
    event: EVENT,
  };
}
